import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { cssVariables } from "@/utils/utils";
import { Button, Flex, Space, Tag, Tooltip } from "antd";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { resolveInstitutionLogo } from "../../../../../../institution/utils";
import { ErrorAlert } from "../../../../components/calc-result/ErrorAlert";
import { RecommendationButton } from "../../../../components/calc-result/RecommendationButton";
import { CalcResult } from "../../../../types";
import { TravelTerritorialValidity } from "../../../enums";
import { TravelCalcResultData } from "../../../types";
import styles from "./TravelCalcResult.module.scss";

const TERRITORIAL_VALIDITY_CLASS_NAMES: Record<TravelTerritorialValidity, string | undefined> = {
  [TravelTerritorialValidity.EUROPE]: styles.europe,
  [TravelTerritorialValidity.WORLD_WITHOUT_USA_AND_CANADA]: styles.worldWithoutUsaAndCanada,
  [TravelTerritorialValidity.WORLD]: styles.world,
  [TravelTerritorialValidity.SVK]: styles.svk,
};

type Props = {
  item: CalcResult<TravelCalcResultData>;
  rotate: () => void;
  handleItemRecommendation: (itemId: string, activation: boolean) => void;
};

export const TravelCalcResultInfo = ({ item, rotate, handleItemRecommendation }: Props) => {
  const { t } = useTranslation();

  return (
    <Flex gap="middle" style={{ height: "100%" }}>
      <Flex vertical gap="middle" style={{ marginTop: 10, width: 100 }}>
        <img
          width="100"
          height="60"
          style={{ objectFit: "contain" }}
          src={resolveInstitutionLogo(item.insuranceInstitution)}
          alt="Institution Logo"
        />

        {item.data?.territorialValidity ? (
          <Tooltip title={t("calc.travel.enums.territorialValidity._label")}>
            <div
              className={classNames(
                styles.territorialValidity,
                TERRITORIAL_VALIDITY_CLASS_NAMES[item.data.territorialValidity],
              )}
            >
              <AntIcon type="global" style={{ fontSize: 20 }} />
              {t("calc.travel.enums.territorialValidity." + item.data.territorialValidity)}
            </div>
          </Tooltip>
        ) : undefined}
      </Flex>

      <Flex justify="space-between" vertical flex="1" style={{ minWidth: 0 }}>
        <div>
          <Flex gap="middle" style={{ marginBottom: 8 }}>
            <h2>
              {item.insuranceInstitution.name} {item.coverage}
            </h2>

            <RecommendationButton item={item} handleItemRecommendation={handleItemRecommendation} />
          </Flex>

          <div style={{ maxHeight: 106, overflowY: "auto" }}>
            {item.error ? (
              <ErrorAlert item={item} />
            ) : (
              <>
                <b>{t("contract.enums.insuranceCoverageType._label")}</b>

                <p className="margin-top-tiny margin-bottom-tiny">
                  {item.data?.appliedCoverages
                    .map((coverage) => t("contract.enums.insuranceCoverageType." + coverage.type))
                    .join(", ")}
                </p>

                {item.data?.warnings.length ? (
                  <Tag
                    style={{ minWidth: 0, display: "block", paddingRight: 24 }}
                    icon={<AntIcon type="exclamation" />}
                    color="warning"
                  >
                    <Ellipsis style={{ color: cssVariables.colorYellow }}>{item.data?.warnings.join(" ")}</Ellipsis>
                  </Tag>
                ) : undefined}
              </>
            )}
          </div>
        </div>

        <Space.Compact block>
          <Button style={{ flex: 1 }} disabled={!!item.error} onClick={rotate}>
            {t("calc.travel.results.additionalInfo")}
          </Button>
          <Button style={{ flex: 1 }} disabled={!!item.error} onClick={rotate}>
            {t("common.documents")}
          </Button>
        </Space.Compact>
      </Flex>
    </Flex>
  );
};
